/* eslint-disable @typescript-eslint/no-unused-vars */
import { ComboBox, Item } from "@adobe/react-spectrum";
import {
  RankedTester,
  rankWith,
  RendererProps,
  uiTypeIs,
} from "@jsonforms/core";
import { useJsonForms, withJsonFormsLayoutProps } from "@jsonforms/react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import React, { useContext, useState } from "react";

import { FormContext } from "../context/FormsContext";

// Utils
import { evaluateContextRule, extractPath } from "../utils/commonUtils";

export const AdvancedComboboxRendererTester: RankedTester = rankWith(
  4,
  uiTypeIs("AdvancedCombobox")
);

/**
 * Custom renderer for a AdvancedCombobox .
 */
function AdvancedComboboxRenderer({
  uischema,
  visible,
  enabled,
}: RendererProps) {
  const customSchema = uischema as any;
  const AdvancedComboboxLabel = customSchema?.label;
  const {
    isReadOnly = false,
    formDataKey = "",
    optionObject = {},
    optionValue = "",
    resultKey = "id",
    optionKey = "",
    isRequired: required = false,
    error: errorMessage = "",
  } = customSchema.options || {};
  const [fieldState, setFieldState] = useState({
    selectedValue: "",
    selectedKey: null,
  });

  const [touched, setTouched] = useState(false);
  const jsonFormContext = useJsonForms();
  const data = jsonFormContext?.core?.data || {};
  const { config: { formTouched = false } = {} } = jsonFormContext || {};
  const hasTouched = formTouched || touched;
  React.useEffect(() => {
    if (
      jsonFormContext?.core?.data &&
      jsonFormContext?.core?.data[formDataKey]
    ) {
      const label =
        optionObject.find(
          (item: any) =>
            item[resultKey].toString() ===
            jsonFormContext?.core?.data[formDataKey].toString()
        )[optionValue] ?? "";
      setFieldState({
        selectedValue: label,
        selectedKey: jsonFormContext?.core?.data[formDataKey],
      });
    }
  }, [jsonFormContext?.core?.data]);
  const validationState = () => {
    if (hasTouched) {
      if (fieldState.selectedValue) {
        return "valid";
      }
      return "invalid";
    }
    return undefined;
  };

  // Disabled when there are errors
  const [_, setContext] = useContext<any>(FormContext);

  const onInputChangeHandler = (value: any) => {
    setTouched(true);
    setFieldState((prevState) => ({
      selectedValue: value,
      selectedKey: value === "" ? null : prevState.selectedKey,
    }));
  };

  const onSelectionChangeHandler = (key: any) => {
    setTouched(true);
    const selectedLabel =
      optionObject.find(
        (item: any) => item[optionKey].toString() === key.toString()
      )[optionValue] ?? "";
    setFieldState({
      selectedValue: selectedLabel || "",
      selectedKey: key,
    });
    setContext((oldValues: any) => ({
      ...oldValues,
      data: {
        ...data,
        ...{ [formDataKey]: resultKey === "id" ? key : selectedLabel },
      },
    }));
  };

  // Determine if the component should be visible and enabled
  let componentVisibility = visible;
  let componentEnabled = enabled;

  // If there is a context rule, evaluate it and use that visibility/enabled rule instead.
  if (uischema.options?.contextRule) {
    const scope = extractPath(uischema.options?.contextRule?.condition?.scope);
    const reduxState = useSelector((state: any) =>
      get(state?.reduxState, scope)
    );
    const contextRuleValidation = evaluateContextRule(
      uischema.options?.contextRule,
      reduxState
    );
    componentVisibility = contextRuleValidation?.visible;
    componentEnabled = contextRuleValidation?.enabled;
  }

  return (
    <ComboBox
      data-testid="advancedCombobox"
      isReadOnly={isReadOnly}
      isRequired={required}
      isDisabled={!componentEnabled}
      aria-label={`AdvancedCombobox-${AdvancedComboboxLabel}`}
      label={AdvancedComboboxLabel}
      defaultItems={optionObject}
      errorMessage={errorMessage}
      validationState={validationState()}
      width="100%"
      inputValue={fieldState.selectedValue}
      onInputChange={onInputChangeHandler}
      selectedKey={fieldState.selectedKey}
      onSelectionChange={onSelectionChangeHandler}
      isHidden={!componentVisibility}
    >
      {(item: any) => <Item key={item[optionKey]}>{item[optionValue]}</Item>}
    </ComboBox>
  );
}

export default withJsonFormsLayoutProps(AdvancedComboboxRenderer);
